/* eslint-disable jsx-a11y/label-has-associated-control */
import { useRef } from 'react';

import PropTypes from 'prop-types';
import shortid from 'shortid';

import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

import { useTransl } from 'shared/lib/i18n';

import ExpandableImageComponent from '../Image/ExpandableImageComponent';
import UploadPreviewComponent from './UploadPreviewComponent';

import StyledTextField from '../Shared/StyledTextField';

const StyledInputOverlay = styled('div')(({ disabled }) => ({
  ...(disabled
    ? { width: '100%', height: '4rem', position: 'absolute', zIndex: 1 }
    : {
        width: '100%',
        height: '4rem',
        position: 'absolute',
        zIndex: 1,
        cursor: 'pointer',
      }),
}));

const StyledInput = styled('input')(() => ({
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  opacity: 0,
  overflow: 'hidden',
  position: 'absolute',
}));

const UploadComponent = (props) => {
  const { t } = useTransl();
  const {
    input: { onBlur, value },
    meta: { touched, error },
    accept,
    isMulti,
    disabled,
    selectedFiles,
    addItem,
    removeItem,
    placeholder = t('title.browse'),
    isReadOnly,
  } = props;

  const inputId = useRef(`upload_component_${shortid.generate()}`);

  return (
    <>
      <Box zIndex="0" position="relative">
        <label htmlFor={inputId.current}>
          <StyledInputOverlay disabled={disabled} />
          <StyledTextField
            value={placeholder}
            helperText={touched && error}
            error={touched && !!error}
            disabled={disabled}
          />
        </label>
        <StyledInput
          type="file"
          id={inputId.current}
          accept={accept}
          multiple={isMulti}
          onClick={(e) => {
            e.target.value = '';
          }}
          onChange={(args) => addItem(args)}
          onBlur={() => onBlur && onBlur(value)}
          disabled={disabled}
        />
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isMulti
            ? { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }
            : '1fr',
          gap: '2rem',
        }}
      >
        {selectedFiles?.length > 0 &&
          selectedFiles?.map((file, index) => {
            // in some cases (amenity update, community post)
            // file is a JS object (url, thumbnailUrl keys)
            // returned from api but not the File object (name, size, imageUrl keys)
            const { name, size, imageUrl, thumbnailUrl, url, id } = file || {};

            if (url || thumbnailUrl) {
              return (
                <Box
                  sx={{ display: 'flex', alignItems: 'flex-end', mt: '1rem' }}
                  key={url || thumbnailUrl}
                >
                  <ExpandableImageComponent
                    src={url || thumbnailUrl}
                    height="15rem"
                    caption={name}
                    isSquare
                  />
                </Box>
              );
            }
            return (
              <UploadPreviewComponent
                key={id || url || name + size}
                name={name}
                size={size}
                url={imageUrl || thumbnailUrl || url}
                file={file}
                onRemoveItem={
                  removeItem && !isReadOnly ? () => removeItem(index) : null
                }
              />
            );
          })}
      </Box>
    </>
  );
};

UploadComponent.defaultProps = {
  input: {},
  meta: {},
  isMulti: false,
  selectedFiles: [],
  removeItem: null,
};

UploadComponent.propTypes = {
  disabled: PropTypes.bool,
  accept: PropTypes.string.isRequired,
  addItem: PropTypes.func.isRequired,
  input: PropTypes.instanceOf(Object),
  meta: PropTypes.instanceOf(Object),
  selectedFiles: PropTypes.instanceOf(Array),
  isMulti: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  removeItem: PropTypes.func,
  placeholder: PropTypes.string,
};

export default UploadComponent;
